import { KeySpecifier } from '@apollo/client/cache/inmemory/policies'

interface IOffsetPaginationArgs {
  pagination?: { offset?: number; limit?: number } | null
}

interface IMergeContext {
  args: IOffsetPaginationArgs | null
}

/**
 * Field policy para lista paginada por `pagination: { offset, limit }` que o
 * componente consome INTEIRA (não página a página).
 *
 * Por que no cache e não no componente: sem merge, um `refetch` — foco de tela ou
 * o `network` do `cache-and-network` — reexecuta a query com as variáveis
 * ORIGINAIS (offset 0) e SOBRESCREVE no cache a lista que o `fetchMore` havia
 * concatenado. Em `ProgressCard.lessonsProgresses` isso deixava o aluno vendo
 * como "não concluída" toda aula fora da primeira página. Com o merge, offset 0
 * reescreve só as primeiras posições e a cauda continua lá — a lista nunca
 * encolhe e não há o que repaginar.
 *
 * `keyArgs` recebe só a ordenação: a paginação FORA da chave é o que faz todas as
 * páginas caírem no mesmo campo do cache; a ordenação DENTRO, porque muda o que
 * cada índice significa.
 */
export const offsetMergeFieldPolicy = (keyArgs: KeySpecifier) => ({
  keyArgs,
  merge(
    existing: unknown[] = [],
    incoming: unknown[] = [],
    { args }: IMergeContext,
  ) {
    const offset = args?.pagination?.offset ?? 0
    // Página fora de ordem: acontece quando a resposta de um `fetchMore` chega
    // depois de o campo já ter mudado embaixo dela. Escrever em `offset >
    // length` abriria buracos `undefined` no meio do array — e o primeiro
    // `.find`/`.map` a seguir explode. Descartar é seguro: o offset volta a ser
    // pedido, porque a contagem não avançou.
    if (offset > existing.length) return existing

    const merged = existing.slice(0)
    for (let index = 0; index < incoming.length; index += 1) {
      merged[offset + index] = incoming[index]
    }
    return merged
  },
})
